import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { API_BASE_URL, STAFF_API, STAFF_BASIC_API } from "utilities";
import { queryClient } from "utilities/queryClient";

const authHeader = () => ({ Authorization: localStorage.getItem("token") });

export const staffKeys = {
  all: ["staff"],
  list: (params) => ["staff", "list", params],
  basic: () => ["staff", "basic"],
  detail: (id) => ["staff", "detail", id],
};

const fetchStaff = (params = {}) =>
  axios
    .get(`${API_BASE_URL}${STAFF_API}`, { headers: authHeader(), params })
    .then((r) => r.data);

const fetchBasicStaff = () =>
  axios
    .get(`${API_BASE_URL}${STAFF_BASIC_API}`, { headers: authHeader() })
    .then((r) => r.data);

/** Full staff list with optional filters. */
export function useStaff(params = {}, options = {}) {
  return useQuery({
    queryKey: staffKeys.list(params),
    queryFn: () => fetchStaff(params),
    staleTime: 5 * 60 * 1000, // staff list changes infrequently
    ...options,
  });
}

/** Lightweight staff list (name + id only) for dropdowns. */
export function useBasicStaff(options = {}) {
  return useQuery({
    queryKey: staffKeys.basic(),
    queryFn: fetchBasicStaff,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/** Create a staff member. */
export function useCreateStaff(options = {}) {
  return useMutation({
    mutationFn: (data) =>
      axios
        .post(`${API_BASE_URL}${STAFF_API}`, data, { headers: authHeader() })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
    },
    ...options,
  });
}

/** Update a staff member. */
export function useUpdateStaff(options = {}) {
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      axios
        .patch(`${API_BASE_URL}${STAFF_API}${id}/`, data, { headers: authHeader() })
        .then((r) => r.data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      queryClient.invalidateQueries({ queryKey: staffKeys.detail(id) });
    },
    ...options,
  });
}
